/**
 * Posturax Variant Selector Component
 * Maneja la selección de variantes, bundles y add to cart
 */

class PosturaxVariantSelector extends HTMLElement {
  constructor() {
    super();
    
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;
    
    this.variantData = null;
    this.currentVariant = null;
    this.selectedOptions = {};
    this.quantity = 1;
    this.bundleQuantity = 1;
    this.bundleDiscount = 0;
    
    this.initElements();
    this.init();
  }

  initElements() {
    // Elementos principales
    this.priceDisplay = this.querySelector('[data-price-display]');
    this.mainImage = this.querySelector('[data-main-image]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.loadingSpinner = this.querySelector('.posturax-variant-selector__loading');
    this.buttonText = this.querySelector('.posturax-variant-selector__button-text');
    
    // Cantidad
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.quantityMinus = this.querySelector('[data-quantity-minus]');
    this.quantityPlus = this.querySelector('[data-quantity-plus]');
    
    // Opciones de variantes
    this.optionInputs = this.querySelectorAll('.posturax-variant-selector__option-input');
    this.selectedValueElements = this.querySelectorAll('[data-selected-]');
    
    // Bundle options
    this.bundleInputs = this.querySelectorAll('.posturax-variant-selector__bundle-input');
    this.bundlePriceElements = this.querySelectorAll('[data-bundle-price]');
  }

  init() {
    this.loadVariantData();
    this.bindEvents();
    this.updateSelectedOptions();
    this.updateCurrentVariant();
    this.updateBundlePrices();
    this.updateFormFields();
  }

  loadVariantData() {
    const variantScript = document.querySelector('[data-product-variants]');
    if (variantScript) {
      try {
        this.variantData = JSON.parse(variantScript.textContent);
        if (this.variantData && this.variantData.length > 0) {
          this.currentVariant = this.variantData.find(v => v.available) || this.variantData[0];
        }
      } catch (error) {
        console.error('Error parsing variant data:', error);
      }
    }
  }

  bindEvents() {
    // Quantity controls
    this.quantityMinus?.addEventListener('click', () => this.updateQuantity(-1));
    this.quantityPlus?.addEventListener('click', () => this.updateQuantity(1));
    this.quantityInput?.addEventListener('change', (e) => {
      this.quantity = parseInt(e.target.value) || 1;
      this.updateQuantityButtons();
    });

    // Variant options
    this.optionInputs.forEach(input => {
      input.addEventListener('change', () => this.handleVariantChange());
    });

    // Bundle options
    this.bundleInputs.forEach(input => {
      input.addEventListener('change', () => this.handleBundleChange(input));
    });

    // Add to cart - disabled in favor of AJAX form submission
    // this.addButton?.addEventListener('click', () => this.addToCart());
  }

  updateQuantity(change) {
    const newQuantity = this.quantity + change;
    if (newQuantity >= 1) {
      this.quantity = newQuantity;
      if (this.quantityInput) {
        this.quantityInput.value = this.quantity;
      }
      this.updateQuantityButtons();
    }
  }

  updateQuantityButtons() {
    if (this.quantityMinus) {
      this.quantityMinus.disabled = this.quantity <= 1;
    }
  }

  updateSelectedOptions() {
    this.optionInputs.forEach(input => {
      if (input.checked) {
        const position = input.dataset.optionPosition;
        const value = input.dataset.optionValue;
        this.selectedOptions[position] = value;
        
        // Update selected value display
        const selectedElement = this.querySelector(`[data-selected-option${position}]`);
        if (selectedElement) {
          selectedElement.textContent = value;
        }
      }
    });
  }

  handleVariantChange() {
    this.updateSelectedOptions();
    this.updateCurrentVariant();
    this.updatePrice();
    this.updateImage();
    this.updateAvailability();
    this.updateBundlePrices();
    
    // Dispatch event for other components (like sticky add-to-cart)
    this.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variant: this.currentVariant }
    }));
  }

  updateCurrentVariant() {
    if (!this.variantData) return;
    
    // Find matching variant based on selected options
    this.currentVariant = this.variantData.find(variant => {
      return Object.keys(this.selectedOptions).every(position => {
        const optionValue = this.selectedOptions[position];
        return variant[`option${position}`] === optionValue;
      });
    });
    
    if (!this.currentVariant) {
      this.currentVariant = this.variantData[0];
    }
  }

  updatePrice() {
    if (!this.currentVariant || !this.priceDisplay) return;
    
    const price = this.currentVariant.price;
    const comparePrice = this.currentVariant.compare_at_price;
    
    let priceHTML = '';
    
    if (comparePrice && comparePrice > price) {
      const discount = Math.round(((comparePrice - price) / comparePrice) * 100);
      priceHTML = `
        <span class="posturax-variant-selector__price--sale">${this.formatMoney(price)}</span>
        <span class="posturax-variant-selector__price--compare">${this.formatMoney(comparePrice)}</span>
        <span class="posturax-variant-selector__discount">${discount}% OFF</span>
      `;
    } else {
      priceHTML = `<span class="posturax-variant-selector__price--regular">${this.formatMoney(price)}</span>`;
    }
    
    this.priceDisplay.innerHTML = priceHTML;
  }

  updateImage() {
    if (!this.currentVariant || !this.mainImage) return;
    
    if (this.currentVariant.featured_media && this.currentVariant.featured_media.preview_image) {
      const newSrc = this.currentVariant.featured_media.preview_image.src;
      if (newSrc !== this.mainImage.src) {
        this.mainImage.src = newSrc;
        this.mainImage.alt = this.currentVariant.name || this.currentVariant.title;
      }
    }
  }

  updateAvailability() {
    if (!this.currentVariant || !this.addButton) return;
    
    if (this.currentVariant.available) {
      this.addButton.disabled = false;
      if (this.buttonText) {
        this.buttonText.textContent = this.addButton.dataset.addText || 'Agregar al carrito';
      }
    } else {
      this.addButton.disabled = true;
      if (this.buttonText) {
        this.buttonText.textContent = 'Agotado';
      }
    }
    
    // Update form fields for AJAX cart
    this.updateFormFields();
  }

  updateFormFields() {
    if (!this.currentVariant) return;
    
    // Update variant ID in form
    const variantIdInput = this.querySelector('#posturax-bundle-variant-id');
    if (variantIdInput) {
      variantIdInput.value = this.currentVariant.id;
    }
    
    // Update quantity in form
    const quantityInput = this.querySelector('#posturax-bundle-qty');
    if (quantityInput) {
      quantityInput.value = Math.max(this.quantity, this.bundleQuantity);
    }
  }

  handleBundleChange(selectedInput) {
    this.bundleQuantity = parseInt(selectedInput.dataset.bundleQuantity) || 1;
    this.bundleDiscount = parseInt(selectedInput.dataset.bundleDiscount) || 0;
    
    // Update main quantity to match bundle
    this.quantity = this.bundleQuantity;
    if (this.quantityInput) {
      this.quantityInput.value = this.quantity;
    }
    this.updateQuantityButtons();
    
    // Update form fields for AJAX cart
    this.updateFormFields();
  }

  updateBundlePrices() {
    if (!this.currentVariant) return;
    
    this.bundlePriceElements.forEach(priceElement => {
      const bundleOption = priceElement.closest('.posturax-variant-selector__bundle-option');
      const bundleInput = bundleOption?.querySelector('.posturax-variant-selector__bundle-input');
      
      if (bundleInput) {
        const quantity = parseInt(bundleInput.dataset.bundleQuantity) || 1;
        const discount = parseInt(bundleInput.dataset.bundleDiscount) || 0;
        
        const totalPrice = this.currentVariant.price * quantity;
        const discountAmount = Math.round(totalPrice * discount / 100);
        const finalPrice = totalPrice - discountAmount;
        
        let priceHTML = '';
        
        if (discount > 0) {
          priceHTML = `
            <span class="posturax-variant-selector__bundle-price--sale">${this.formatMoney(finalPrice)}</span>
            <span class="posturax-variant-selector__bundle-price--compare">${this.formatMoney(totalPrice)}</span>
          `;
        } else {
          priceHTML = `<span class="posturax-variant-selector__bundle-price--regular">${this.formatMoney(totalPrice)}</span>`;
        }
        
        priceElement.innerHTML = priceHTML;
      }
    });
  }

  // addToCart function removed - now handled by posturax-ajax-cart.js

  setLoading(loading) {
    if (this.addButton) {
      this.addButton.classList.toggle('is-loading', loading);
      this.addButton.disabled = loading || !this.currentVariant?.available;
    }
  }

  showSuccess() {
    // Show success message
    const originalText = this.buttonText?.textContent;
    if (this.buttonText) {
      this.buttonText.textContent = '¡Agregado!';
      
      // Show success notification
      this.showNotification('¡Producto agregado al carrito!', 'success');
      
      setTimeout(() => {
        if (this.buttonText) {
          this.buttonText.textContent = originalText;
        }
      }, 2000);
    }
  }

  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `posturax-notification posturax-notification--${type}`;
    notification.textContent = message;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10B981' : '#EF4444'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      font-weight: 600;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  showError() {
    console.error('Error al agregar al carrito');
    // Could implement error UI here
  }

  // updateCartIcon function removed - now handled by posturax-ajax-cart.js

  formatMoney(cents) {
    // Basic money formatting - could be enhanced with Shopify's money filters
    const amount = cents / 100;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  }

  // Public API for external access
  getSelectedVariant() {
    return this.currentVariant;
  }

  setVariant(variantId) {
    const variant = this.variantData?.find(v => v.id === variantId);
    if (variant) {
      this.currentVariant = variant;
      this.updatePrice();
      this.updateImage();
      this.updateAvailability();
    }
  }

  getSelectedOptions() {
    return this.selectedOptions;
  }
}

// Register the custom element
if (!customElements.get('posturax-variant-selector')) {
  customElements.define('posturax-variant-selector', PosturaxVariantSelector);
}

// Global function for external access
window.PosturaxVariants = {
  getSelector() {
    return document.querySelector('posturax-variant-selector');
  },
  
  getSelectedVariant() {
    const selector = this.getSelector();
    return selector?.getSelectedVariant();
  },
  
  selectVariant(variantId) {
    const selector = this.getSelector();
    return selector?.setVariant(variantId);
  }
};

export default PosturaxVariantSelector;
